import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./NephroprotectionAndRrtSection.css";

const nephroprotection = [
  { key: "bsra", title: "raas", detail: "raasDetail" },
  { key: "sglt", title: "sglt", detail: "sgltDetail" },
  { key: "finerenone", title: "finerenone", detail: "finerenoneDetail" },
];

const nephrotoxicity = [
  "nsaids", "contrast", "selfMedication", "otherNephrotoxins",
];

const vaccines = ["covid", "influenza", "pneumococcal", "hepatitis"];
const modalities = [
  { key: "hd", value: "HD" },
  { key: "pd", value: "DP" },
  { key: "transplant", value: "TR" },
];
const rrtPreparations = [
  { key: "dopplerMs", title: "doppler", detail: "dopplerDetail" },
  { key: "fav", title: "fistula", detail: "fistulaDetail" },
  { key: "capitauxVineux", title: "venous", detail: "venousDetail" },
];
const procedures = ["renalBiopsy", "ktSimple", "ktTunnel", "abpm", "ultrasound"];

const containsSavedValue = (values, key, legacyValue) =>
  Array.isArray(values) && (values.includes(key) || values.includes(legacyValue));

function Toggle({ checked, onChange, label, fieldKey, loadedChecked }) {
  return (
    <button
      type="button"
      role="switch"
      aria-label={label}
      aria-checked={checked}
      data-nephro-switch={fieldKey}
      data-loaded-checked={String(loadedChecked)}
      className={`nephro-rrt__switch${checked ? " is-on" : ""}`}
      onClick={() => onChange(!checked)}
    >
      <span />
    </button>
  );
}

export default function NephroprotectionAndRrtSection({
  nephroprotectionRecord,
  nephroprotectionLoading,
  nephroprotectionError,
}) {
  const { t } = useTranslation("neurologue");
  const translateRef = useRef(t);
  const [protection, setProtection] = useState([false, false, false]);
  const [protectionDates, setProtectionDates] = useState(["", "", ""]);
  const [toxins, setToxins] = useState([]);
  const [doseAdjustment, setDoseAdjustment] = useState(false);
  const [vaccineStatus, setVaccineStatus] = useState([]);
  const [modality, setModality] = useState("");
  const [preparations, setPreparations] = useState([false, false, false]);
  const [saltTarget, setSaltTarget] = useState("");
  const [proteinTarget, setProteinTarget] = useState("");
  const [selectedProcedures, setSelectedProcedures] = useState([]);
  translateRef.current = t;

  useEffect(() => {
    const record = nephroprotectionRecord || {};
    const savedToxins = record.nephroAins || [];
    const savedVaccines = record.vaccination || [];
    setProtection(nephroprotection.map(({ key }) => Boolean(record[key])));
    setProtectionDates(nephroprotection.map(({ key }) => String(record[`${key}Date`] || "").slice(0, 10)));
    setToxins(nephrotoxicity.filter((key) =>
      containsSavedValue(savedToxins, key, translateRef.current(`nephro.${key}`)),
    ));
    setDoseAdjustment(Boolean(record.posologie?.length));
    setVaccineStatus(vaccines.filter((key) =>
      containsSavedValue(savedVaccines, key, translateRef.current(`nephro.${key}`)),
    ));
    setModality(record.presuppleance || "");
    setPreparations(rrtPreparations.map(({ key }) => Boolean(record[key])));
    setSaltTarget(record.saltIntake == null ? "" : String(record.saltIntake));
    setProteinTarget(record.proteinTarget == null ? "" : String(record.proteinTarget));
    setSelectedProcedures(Array.isArray(record.medicalActs) ? record.medicalActs : []);
  }, [nephroprotectionRecord]);

  const updateList = (setter, index, value) => {
    setter((current) => current.map((item, itemIndex) => itemIndex === index ? value : item));
  };

  const proteinTotal = (Number.parseFloat(proteinTarget) || 0) * 91;

  return (
    <section
      className="neurologue-section-card nephro-rrt"
      data-nephro-record-id={nephroprotectionRecord?.id || ""}
    >
      {nephroprotectionLoading && <p role="status">{t("common.loading", { defaultValue: "Loading…" })}</p>}
      {nephroprotectionError && <p role="alert">{nephroprotectionError}</p>}
      <div className="nephro-rrt__section">
        <h3>{t("nephro.title")}</h3>
        <div className="nephro-rrt__protection-list">
          {nephroprotection.map((item, index) => (
            <div className="nephro-rrt__protection-row" key={item.key}>
              {Toggle({
                checked: protection[index],
                onChange: (value) => updateList(setProtection, index, value),
                label: t(`nephro.${item.title}`),
                fieldKey: item.key,
                loadedChecked: Boolean(nephroprotectionRecord?.[item.key]),
              })}
              <div><strong>{t(`nephro.${item.title}`)}</strong><small>{t(`nephro.${item.detail}`)}</small></div>
              {protection[index] && <input
                className="nephro-rrt__treatment-date"
                type="date"
                data-nephro-date={item.key}
                aria-label={t("nephro.treatmentDate", { defaultValue: "Date de début du traitement" })}
                value={protectionDates[index]}
                onChange={(event) => setProtectionDates((current) => current.map((date, dateIndex) => dateIndex === index ? event.target.value : date))}
              />}
            </div>
          ))}
        </div>
      </div>

      <fieldset className="nephro-rrt__toxins">
        <legend>{t("nephro.toxicityReview")}</legend>
        <div className="nephro-rrt__check-grid">
          {nephrotoxicity.map((item) => (
            <label className="nephro-rrt__check" key={item}>
              <input type="checkbox" data-nephro-toxin={item} checked={toxins.includes(item)} onChange={(event) => setToxins((current) => event.target.checked ? [...current, item] : current.filter((value) => value !== item))} />
              <span>{t(`nephro.${item}`)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <div className="nephro-rrt__dose-row">
        <label className="nephro-rrt__check">
          <input type="checkbox" data-nephro-dose-adjustment checked={doseAdjustment} onChange={(event) => setDoseAdjustment(event.target.checked)} />
          <span>{t("nephro.doseAdjustment")}</span>
        </label>
        <span className="nephro-rrt__info">{t("nephro.doseInfo")}</span>
      </div>

      <fieldset className="nephro-rrt__vaccines">
        <legend>{t("nephro.vaccination")}</legend>
        <div className="nephro-rrt__vaccine-grid">
          {vaccines.map((item) => (
            <label className="nephro-rrt__check" key={item}>
              <input type="checkbox" data-nephro-vaccine={item} checked={vaccineStatus.includes(item)} onChange={(event) => setVaccineStatus((current) => event.target.checked ? [...current, item] : current.filter((value) => value !== item))} />
              <span>{t(`nephro.${item}`)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="nephro-rrt__rrt">
        <legend>{t("nephro.rrtPreparation")}</legend>
        <div className="nephro-rrt__sub-label">{t("nephro.modality")}</div>
        <div className="nephro-rrt__modalities" role="group" aria-label={t("nephro.modalityAria")}>
          {modalities.map((item) => (
            <button type="button" data-nephro-modality={item.value} className={modality === item.value ? "is-active" : ""} aria-pressed={modality === item.value} key={item.key} onClick={() => setModality((current) => current === item.value ? "" : item.value)}>{t(`nephro.${item.key}`)}</button>
          ))}
        </div>
        <div className="nephro-rrt__preparation-list">
          {rrtPreparations.map((item, index) => (
            <div className="nephro-rrt__preparation-row" key={item.key}>
              {Toggle({
                checked: preparations[index],
                onChange: (value) => updateList(setPreparations, index, value),
                label: t(`nephro.${item.title}`),
                fieldKey: item.key,
                loadedChecked: Boolean(nephroprotectionRecord?.[item.key]),
              })}
              <div><strong>{t(`nephro.${item.title}`)}</strong><small>{t(`nephro.${item.detail}`)}</small></div>
            </div>
          ))}
        </div>
      </fieldset>

      <div className="nephro-rrt__section nephro-rrt__diet">
        <h3>{t("nephro.diet")}</h3>
        <div className="nephro-rrt__diet-grid">
          <label className="nephro-rrt__diet-field">
            <span>{t("nephro.saltTarget")}</span>
            <span className="nephro-rrt__unit-input"><input type="number" min="0" step="0.5" data-nephro-diet="saltIntake" value={saltTarget} onChange={(event) => setSaltTarget(event.target.value)} /><i>{t("nephro.gDay")}</i></span>
            <small>{t("nephro.recommendedSalt")}</small>
          </label>
          <label className="nephro-rrt__diet-field">
            <span>{t("nephro.proteinTarget")}</span>
            <span className="nephro-rrt__unit-input"><input type="number" min="0" step="0.1" data-nephro-diet="proteinTarget" value={proteinTarget} onChange={(event) => setProteinTarget(event.target.value)} /><i>{t("nephro.gKgDay")}</i></span>
            <small>{t("nephro.recommendedProtein")}</small>
          </label>
          <div className="nephro-rrt__protein-total">
            <span>{t("nephro.calculatedProtein")}</span>
            <strong>{t("nephro.dailyProtein", { value: proteinTotal.toFixed(0) })}</strong>
            <small>{t("nephro.proteinFormula", { protein: proteinTarget })}</small>
          </div>
        </div>
      </div>

      <div className="nephro-rrt__procedures">
        <h3>{t("nephro.procedures")}</h3>
        <div className="nephro-rrt__procedure-list">
          {procedures.map((item) => (
            <button
              type="button"
              data-nephro-procedure={item}
              className={selectedProcedures.includes(item) ? "is-selected" : ""}
              aria-pressed={selectedProcedures.includes(item)}
              key={item}
              onClick={() => setSelectedProcedures((current) => current.includes(item) ? current.filter((value) => value !== item) : [...current, item])}
            >+ {t(`nephro.${item}`)}<small>{t("nephro.medicalAct")}</small></button>
          ))}
        </div>
      </div>
    </section>
  );
}
